import React, { useState } from "react";
import Papa from "papaparse";
import styles from "./styles";

const REQUIRED_HEADERS = [
  "Order ID",
  "Order Date",
  "User Email",
  "Product Name",
  "Quantity",
  "Price",
  "Shipping Address",
];

const CsvUploadStep = ({ onNext, onBack }) => {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [previewData, setPreviewData] = useState([]);

  const handleFileUpload = (e) => {
    const csvFile = e.target.files[0];
    if (!csvFile || !csvFile.name.endsWith(".csv")) {
      setError("Only .csv files are allowed.");
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => validateAndTransform(results.data),
      error: (err) => setError(`Error parsing file: ${err.message}`),
    });

    setFiles([csvFile]);
    setError("");
  };

  const validateAndTransform = (data) => {
    if (!data.length) {
      setError("The CSV file is empty or invalid.");
      setPreviewData([]);
      return;
    }

    const headers = Object.keys(data[0]);
    const missingHeaders = REQUIRED_HEADERS.filter((header) => !headers.includes(header));
    if (missingHeaders.length) {
      setError(`Missing required headers: ${missingHeaders.join(", ")}`);
      setPreviewData([]);
      return;
    }

    const transformedData = data.map((row) => ({
      "Order ID": row["Order ID"],
      "Order Date": row["Order Date"],
      "User Email": row["User Email"],
      "Product Name": row["Product Name"],
      "Quantity": parseInt(row["Quantity"], 10),
      "Price": parseFloat(row["Price"]).toFixed(2),
      "Total Price": (parseInt(row["Quantity"], 10) * parseFloat(row["Price"])).toFixed(2),
      "Shipping Address": row["Shipping Address"],
    }));

    setPreviewData(transformedData);
  };

  const handleFileRemove = () => {
    setFiles([]);
    setPreviewData([]);
  };

  const handleSubmit = async () => {
    console.log("handle submit called for csv");
    if (!files.length) {
      setError("Please upload a file to continue.");
      return;
    }

    const formData = new FormData();
    formData.append("file", files[0]);

    const token = localStorage.getItem("token"); // Ensure token is stored during signup/login
    if (!token) {
      setError("Authentication token is missing. Please log in again.");
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8080";
    console.log("Using API URL csv:", apiUrl);

    try {
      const response = await fetch(`${apiUrl}/api/csv/upload-csv`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`, // Include the Bearer token
        },
        body: formData, // Attach the file
      });

      if (response.ok) {
        const result = await response.json();
        console.log("File uploaded successfully:", result);
        onNext(); // Proceed to the next step
      } else {
        const error = await response.json();
        setError(error.detail || "Failed to upload the file. Please try again.");
      }
    } catch (err) {
      console.error("Error uploading file:", err);
      setError("An error occurred during file upload. Please try again.");
    }
  };


  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "85vh",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
      }}
    >
      <h2 style={{ fontSize: "24px", fontWeight: "bold", marginBottom: "10px" }}>Upload Your Data</h2>
      <p style={{ fontSize: "14px", margin: "10px 0", textAlign: "center", maxWidth: "800px" }}>
        To get the most value, upload data from Shopify in the required format. Use the <a href="/sample-template.csv" style={{ color: "#007bff", textDecoration: "underline" }}>sample template</a> to ensure your data format is correct.
      </p>


      <div
        style={{
          display: "flex",
          width: "100%",
          maxWidth: "1000px",
          height: "70vh",
          border: "1px solid #ccc",
          borderRadius: "10px",
          backgroundColor: "#fff",
          overflow: "hidden",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div style={{ flex: "1", padding: "20px", backgroundColor: "#f9f9f9", borderRight: "1px solid #ddd" }}>
          <h4 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "10px" }}>File Details</h4>
          {files.length > 0 ? (
            <>
              <p style={{ fontSize: "14px", marginBottom: "10px" }}>
                <strong>Uploaded File:</strong> {files[0].name}
              </p>
              <button
                onClick={handleFileRemove}
                style={{
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  padding: "8px 15px",
                  cursor: "pointer",
                }}
              >
                Remove File
              </button>
            </>
          ) : (
            <input
              type="file"
              onChange={handleFileUpload}
              style={{
                padding: "10px",
                border: "1px solid #ccc",
                borderRadius: "5px",
                width: "100%",
              }}
              accept=".csv"
            />
          )}
          {error && <p style={{ color: "red", marginTop: "10px", fontSize: "14px" }}>{error}</p>}
        </div>

        <div style={{ flex: "2", padding: "20px", overflow: "auto" }}>
          {previewData.length > 0 ? (
            <div>
              <h4 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "10px" }}>Preview Transformed Data</h4>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                  tableLayout: "fixed",
                }}
              >
                <thead>
                  <tr>
                    {Object.keys(previewData[0]).map((key) => (
                      <th
                        key={key}
                        style={{
                          borderBottom: "1px solid #ccc",
                          padding: "10px",
                          backgroundColor: "#f4f4f4",
                          textAlign: "left",
                        }}
                      >
                        {key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(0, 8).map((row, index) => (
                    <tr
                      key={index}
                      style={{
                        backgroundColor: index % 2 === 0 ? "#fafafa" : "#fff",
                      }}
                    >
                      {Object.values(row).map((value, idx) => (
                        <td
                          key={idx}
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={value}
                        >
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: "12px", marginTop: "10px" }}>Showing first 8 rows...</p>
            </div>
          ) : (
            <p>No preview available. Upload a file to see data.</p>
          )}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px", width: "100%", maxWidth: "1000px" }}>
        <button
          onClick={onBack}
          style={{
            backgroundColor: "#6c757d",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            padding: "8px 15px",
            cursor: "pointer",
          }}
        >
          Back
        </button>
        <button
           onClick={() => {
            console.log("Next button clicked");
            handleSubmit();
          }}
          disabled={false} // Ensure the button is not disabled
          style={{
            backgroundColor: "#28a745",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            padding: "8px 15px",
            cursor: "pointer",
          }}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default CsvUploadStep;
